import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { hideHeaderRoutes } from '../constants/navigation';

export const useAppUpdate = () => {
  const [currentVersion, setCurrentVersion] = useState(null);
  const [latestVersion, setLatestVersion] = useState(null);
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const location = useLocation();

  // Check for updates
  const checkForUpdates = useCallback(async () => {
    try {
      // Fetch the latest version from the server
      const response = await fetch('/version.json', {
        cache: 'no-cache', // Always fetch fresh version
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        console.warn('Failed to fetch version info');
        return;
      }

      const versionData = await response.json();
      const serverVersion = versionData.version;
      const serverBuildTime = versionData.buildTime;

      // Store the latest build time for later use
      localStorage.setItem('latest_build_time', serverBuildTime);

      // Get current version and build time from local storage
      const storedVersion = localStorage.getItem('app_version');
      const storedBuildTime = localStorage.getItem('app_build_time');

      if (!storedVersion) {
        // First time - store the current version and build time
        localStorage.setItem('app_version', serverVersion);
        localStorage.setItem('app_build_time', serverBuildTime);
        setCurrentVersion(serverVersion);
        setLatestVersion(serverVersion);
      } else {
        setCurrentVersion(storedVersion);
        setLatestVersion(serverVersion);

        // Check if update is available (version changed OR build time changed)
        const versionChanged = storedVersion !== serverVersion;
        const buildTimeChanged = storedBuildTime !== serverBuildTime;

        if (versionChanged || buildTimeChanged) {
          setIsUpdateAvailable(true);
          // Clear the last notified version since this is a new update
          localStorage.removeItem('last_notified_version');
        } else {
          setIsUpdateAvailable(false);
        }
      }
    } catch (error) {
      console.error('Error checking for updates:', error);
    }
  }, []);

  // Show update notification
  const showUpdateNotification = useCallback(() => {
    if (!isUpdateAvailable) return;
    // Always show the modal when update is available (no dismissal allowed)
    setShowUpdateModal(true);
  }, [isUpdateAvailable]);

  // Handle modal close - force refresh
  const handleModalClose = useCallback(() => {
    // Force refresh when user tries to close/dismiss
    localStorage.setItem('app_version', latestVersion);
    // Also store the latest build time
    const latestBuildTime = localStorage.getItem('latest_build_time');
    if (latestBuildTime) {
      localStorage.setItem('app_build_time', latestBuildTime);
    }
    window.location.reload();
  }, [latestVersion]);

  // Handle refresh action
  const handleRefresh = useCallback(() => {
    // Update version and build time before reload
    localStorage.setItem('app_version', latestVersion);
    // Also store the latest build time
    const latestBuildTime = localStorage.getItem('latest_build_time');
    if (latestBuildTime) {
      localStorage.setItem('app_build_time', latestBuildTime);
    }
    // Clear the "shown this session" flag so the password reminder appears
    // again after the update reload (it is part of the post-update popup flow).
    sessionStorage.removeItem('passwordReminderShownThisSession');
    window.location.reload();
  }, [latestVersion]);

  // Update stored version when user refreshes
  const updateVersion = useCallback(() => {
    if (latestVersion) {
      localStorage.setItem('app_version', latestVersion);
      // Also update the build time
      const latestBuildTime = localStorage.getItem('latest_build_time');
      if (latestBuildTime) {
        localStorage.setItem('app_build_time', latestBuildTime);
      }
      setCurrentVersion(latestVersion);
      setIsUpdateAvailable(false);
    }
  }, [latestVersion]);

  // Check for updates on mount and periodically
  useEffect(() => {
    // Initial check
    checkForUpdates();

    // Check every 1 minute (adjust to 5 minutes for production)
    const interval = setInterval(checkForUpdates, 60 * 1000);

    return () => clearInterval(interval);
  }, [checkForUpdates]);

  // Show notification when update becomes available
  useEffect(() => {
    const normalizedPathname = location.pathname.replace(/\/$/, '');
    const isProtectedRoute = !hideHeaderRoutes.includes(normalizedPathname);

    if (isUpdateAvailable && isProtectedRoute) {
      showUpdateNotification();
    }
  }, [isUpdateAvailable, location.pathname, showUpdateNotification]);

  return {
    currentVersion,
    latestVersion,
    isUpdateAvailable,
    checkForUpdates,
    updateVersion,
    showUpdateModal,
    handleModalClose,
    handleRefresh,
    showUpdateNotification, // Expose this to allow manual triggering
  };
};